import { MESApiService } from "@dexteel/mesf-core";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";

const ALL_CREWS_VALUE = "all";

interface CrewRow {
  Crew: string;
}

interface CrewOption {
  label: string;
  value: string;
}

const getCrews = async (signal?: AbortSignal): Promise<CrewRow[]> => {
  const apiService = new MESApiService();
  const response = await apiService.callV2(
    "[MES].[GetShiftsAndCrews]",
    [],
    signal,
  );
  if (!response.ok) {
    throw new Error(response.message ?? "Failed to fetch Crews");
  }

  return (response.data?.tables?.[1]?.rows ?? []) as CrewRow[];
};

export const useCrewsQuery = () =>
  useQuery({
    queryKey: ["crews"],
    queryFn: ({ signal }) => getCrews(signal),
  });

interface CrewSelectorProps {
  value: string | null;
  onChange: (value: string | null) => void;
}

export const CrewSelector = ({ value, onChange }: CrewSelectorProps) => {
  const { data: crews = [], isLoading } = useCrewsQuery();

  const options: CrewOption[] = [
    { label: "All", value: ALL_CREWS_VALUE },
    ...crews.map((crew) => ({ label: crew.Crew, value: crew.Crew })),
  ];

  const selectedOption =
    options.find((option) => option.value === (value ?? ALL_CREWS_VALUE)) ??
    options[0];

  return (
    <Autocomplete
      value={selectedOption}
      options={options}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, selected) =>
        option.value === selected.value
      }
      onChange={(_event, newValue) =>
        onChange(
          !newValue || newValue.value === ALL_CREWS_VALUE
            ? null
            : newValue.value,
        )
      }
      disabled={isLoading}
      disableClearable
      size="small"
      renderInput={(params) => (
        <TextField
          {...params}
          label="Crew"
          variant="outlined"
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: isLoading ? (
                <CircularProgress color="inherit" size={20} />
              ) : (
                params.InputProps.endAdornment
              ),
            },
          }}
        />
      )}
    />
  );
};
